import React, { useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { FaEnvelope, FaEye, FaEyeSlash, FaGlobe, FaLock, FaMapMarkerAlt, FaPhone, FaUser } from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_API_URL;


function BusinessRegister({ formData: parentFormData, handleChange: parentHandleChange, errors: parentErrors }) {
  // Use the props if available, otherwise use local state
  const [localData, setLocalData] = useState({
    accountType: 'business',
    company: '',
    phoneNumber: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    address: '',
    country: 'lk'
  });
  
  const [localErrors, setLocalErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  
  // Determine whether to use props or local state
  const formData = parentFormData || localData;
  const errors = parentErrors || localErrors;
  const setErrors = parentErrors ? null : setLocalErrors;


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (parentHandleChange) {
      parentHandleChange(e);
    } else {
      setLocalData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSendOTP = async () => {
    try {
      // Validate email first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.emailAddress || !emailRegex.test(formData.emailAddress)) {
        setLocalErrors(prev => ({
          ...prev,
          emailAddress: 'Please enter a valid email address'
        }));
        return;
      }

      setLoading(true);
      const response = await fetch(`${BASE_URL}/send-verification-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emailAddress: formData.emailAddress })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('OTP sent to your email');
        setShowOtpInput(true);
      } else {
        throw new Error(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailAddress: formData.emailAddress,
          otp
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setIsEmailVerified(true);
        setShowOtpInput(false);
        setMessage('Email verified successfully');
        setOtpError('');
      } else {
        setOtpError(data.error || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.company) newErrors.company = 'company name is required';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.emailAddress) newErrors.emailAddress = 'Email is required';
    if (!formData.address) newErrors.address = 'Address is required';

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.emailAddress && !emailRegex.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Invalid email format';
    }

    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    // Strong password validation
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
    
    if (formData.password && !(hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.country) newErrors.country = 'Country is required';

    if (setErrors) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (!isEmailVerified) {
      setMessage('Please verify your email first');
      return;
    }

    // Validation
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/register-business`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          accountType: 'business' // Ensure this is always set
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setLoading(false);
      setMessage(data.message || "Registration successful!");
      
      // Reset form after successful registration
      if (!parentHandleChange) {
        setLocalData({
          accountType: 'business',
          company: '',          
          emailAddress: '',
          password: '',
          confirmPassword: '',
          address: '',
          phoneNumber: '',
          country: 'lk'
          
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setLoading(false);
      setMessage(`Error: ${error.message || "Failed to register. Please try again."}`);
    }
  };

  // Countries list
  const countries = [
    { value: 'lk', label: 'Sri Lanka' },
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'in', label: 'India' },
  ];

  return (
    <Card className="shadow-lg border-0 rounded-lg mb-4">
      <Card.Header className="bg-primary text-white text-center py-3">
        <h3 className="mb-0">Business Registration</h3>
        <p className="text-white-50 mb-0">Create your account</p>
      </Card.Header>
      <Card.Body className="p-4">
        {message && (
          <Alert variant={message.startsWith("Error") ? "danger" : "success"} dismissible>
            {message}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit} noValidate>
          {/* Personal Information Section */}
          <h5 className="border-bottom pb-2 mb-3">Company Information</h5>
          
          {/* First Name and Last Name */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Company Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaUser /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    isInvalid={!!errors.company}
                    placeholder="Enter company name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.company}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            
          </Row>

          {/* Contact Information Section */}
          <h5 className="border-bottom pb-2 mb-3 mt-4">Contact Information</h5>
          
          {/* Phone Number and Email */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Phone Number</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaPhone /></InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.phoneNumber}
                    placeholder="Enter phone number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phoneNumber}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email Address</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    isInvalid={!!errors.emailAddress}
                    placeholder="Enter email address"
                    disabled={isEmailVerified}
                  />
                  <Button
                    variant={isEmailVerified ? "success" : "primary"}
                    onClick={handleSendOTP}
                    disabled={loading || isEmailVerified}
                  >
                    {isEmailVerified ? "Verified" : "Verify Email"}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.emailAddress}
                  </Form.Control.Feedback>
                </InputGroup>
                
              </Form.Group>
            </Col>
            <Col md={6}>
            {showOtpInput && (
                  <Row className="mb-3">
                    
                      <Form.Group>
                        <Form.Label>Enter OTP</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaLock /></InputGroup.Text>
                          <Form.Control
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            isInvalid={!!otpError}
                            placeholder="Enter OTP"
                            maxLength={6}
                          />
                          <Button
                            variant="primary"
                            onClick={handleVerifyOTP}
                            disabled={loading || otp.length !== 6}
                          >
                            Verify OTP
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {otpError}
                          </Form.Control.Feedback>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Check your email for the OTP
                        </Form.Text>
                      </Form.Group>
                   
                  </Row>
                )}
            </Col>
           
          </Row>

          {/* Address and Country */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    isInvalid={!!errors.address}
                    placeholder="Enter your address"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Country</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaGlobe /></InputGroup.Text>
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    isInvalid={!!errors.country}
                  >
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.country}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* Security Section */}
          <h5 className="border-bottom pb-2 mb-3 mt-4">Security</h5>
          
          {/* Password and Confirm Password */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaLock /></InputGroup.Text>
                  <Form.Control
                    type={showPassword ? "text" : "password"} 
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    isInvalid={!!errors.password}
                    placeholder="Create password"
                  />
                  <button
                  type="button"
                  className="input-group-text"
                  onClick={togglePasswordVisibility}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Confirm Password</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaLock /></InputGroup.Text>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"} 
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    isInvalid={!!errors.confirmPassword}
                    placeholder="Confirm password"
                  />
                   <button
                  type="button"
                  className="input-group-text"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{ cursor: 'pointer' }}
                >
                  {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* Plan Selection (Hidden but submitted) */}
          {/* <input type="hidden" name="plan" value={formData.plan} /> */}

          {/* Submit Button */}
          <div className="d-grid gap-2">
            <Button type="submit" variant="primary" size="lg" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </div>
          
          {/* Debug information - remove in production */}
          <div className="mt-3 d-none">
            <pre>{JSON.stringify(formData, null, 2)}</pre>
          </div>
        </Form>
      </Card.Body>
      <Card.Footer className="text-center py-3 bg-light">
        <p className="mb-0">
          Already have an account? <a href="/LoginTabs" className="fw-bold">Log in</a>
        </p>
      </Card.Footer>
    </Card>
  );
}

export default BusinessRegister;